import { Role, type User } from "@cryptors/api-client";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button, Checkbox } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  EditButton,
  ShowButton,
  List,
  EmailField,
  DateField,
  useDataGrid,
} from "@refinedev/mui";
import React, { useMemo, useState } from "react";

import { fetchAllUsers } from "@/api/users.api";
import { DeleteIconButton } from "@/components/forms/DeleteIconButton";
import { SearchField } from "@/components/forms/SearchField";

const toCsv = <TRow extends object>(rows: TRow[]) => {
  if (!rows.length) return "";
  const cols = ["id", "pseudo", "email", "emailConfirmed", "role", "createdAt"];
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const body = rows
    .map((r) => cols.map((c) => escape(r[c as keyof TRow])).join(","))
    .join("\n");
  return `${header}\n${body}`;
};

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ROLE_VALUES: readonly Role[] = Object.values(Role);

const isRole = (value: string): value is Role =>
  (ROLE_VALUES as readonly string[]).includes(value);

const buildSearchFilters = (q: string): CrudFilter[] => {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const upper = trimmed.toUpperCase();
  const orValue: CrudFilter[] = [
    { field: "pseudo", operator: "containss", value: trimmed },
    { field: "email", operator: "containss", value: trimmed },
  ];
  if (isRole(upper)) {
    orValue.push({ field: "role", operator: "eq", value: upper });
  }
  if (/^\d+$/.test(trimmed)) {
    orValue.push({ field: "id", operator: "eq", value: Number(trimmed) });
  }
  return [{ operator: "or", value: orValue }];
};

export const UserList: React.FC<IResourceComponentsProps> = () => {
  const { dataGridProps, setFilters } = useDataGrid<User>({
    resource: "users",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "createdAt", order: "desc" }] },
  });

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = await fetchAllUsers();
      downloadCsv(
        `users-${new Date().toISOString().slice(0, 10)}.csv`,
        toCsv(all),
      );
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<GridColDef<User>[]>(
    () => [
      {
        field: "id",
        headerName: "Id",
        type: "number",
        minWidth: 50,
      },
      {
        field: "pseudo",
        flex: 1,
        headerName: "Pseudo",
        minWidth: 200,
      },
      {
        field: "email",
        flex: 1,
        headerName: "Email",
        minWidth: 250,
        renderCell: function render({ value }) {
          return <EmailField value={value} />;
        },
      },
      {
        field: "emailConfirmed",
        headerName: "Email confirmé",
        minWidth: 130,
        align: "center",
        renderCell: function render({ value }) {
          return <Checkbox checked={!!value} />;
        },
      },
      {
        field: "role",
        headerName: "Role",
        minWidth: 100,
      },
      {
        field: "avatar",
        headerName: "Avatar",
        sortable: false,
        align: "center",
        valueGetter: (_value, row) => {
          const value = row.avatar?.image?.url;
          return value;
        },
        minWidth: 75,
        renderCell: function render({ value }) {
          return <img alt="avatar" src={value} width={50} height={50} />;
        },
      },
      {
        field: "createdAt",
        headerName: "Date de création",
        minWidth: 100,
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteIconButton recordItemId={row.id} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
    ],
    [],
  );

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Export…" : "Export CSV"}
          </Button>
        </>
      )}
    >
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (pseudo, email, role, id)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
